/**
 * 生成キューの行 (モーショングラフィックスのジョブ) に出す候補の並び (docs/features/brand-cut-motion-graphics-plan.md §10.10.1)。
 *
 * - 候補 (check が通った版) ごとにコンタクトシートのサムネイルを横に並べる。クリックで拡大プレビュー (採点があれば平均と要約)
 * - 各サムネイルに「この版にする」、並びの末尾に「ここまでで決める」。押すと行内の二段クリックの確認 (✕ と同じ作法) を出し、
 *   確定で onStop(pick?) を呼ぶ (API 呼び出しと段階表示の切り替えは親の QueueItem)
 * - 署名 URL (1 時間) が切れて画像が読めないときは静かに「画像なし」にする
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { isCandidateScored, type MotionCandidate } from '../../features/motion/jobs';
import styles from './GenerationWorkspace.module.css';

type StopTarget = { pick?: number };

export function MotionCandidateStrip({
  candidates,
  stopped,
  stopping,
  onStop,
}: {
  candidates: MotionCandidate[];
  /** 中断を受け付け済み (ボタンを出さない) */
  stopped: boolean;
  /** stop の送信中 */
  stopping: boolean;
  onStop: (pick?: number) => void;
}) {
  const [confirm, setConfirm] = useState<StopTarget | null>(null);
  const [preview, setPreview] = useState<MotionCandidate | null>(null);
  const [broken, setBroken] = useState<Set<number>>(() => new Set());

  if (candidates.length === 0) return null;
  const canStop = !stopped && !stopping;

  return (
    <div className={styles.motionCandidates} onClick={(e) => e.stopPropagation()} data-testid="motion-candidates">
      <div className={styles.motionCandidateList}>
        {candidates.map((c) => {
          const scored = isCandidateScored(c);
          return (
            <div key={c.round} className={styles.motionCandidate} data-testid={`motion-candidate-${c.round}`}>
              <button
                type="button"
                className={styles.motionCandidateThumbBtn}
                onClick={() => setPreview(c)}
                title="大きく見る"
                aria-label={`版 ${c.round} を大きく見る`}
              >
                {c.contactSheetUrl && !broken.has(c.round) ? (
                  <img
                    src={c.contactSheetUrl}
                    alt={`版 ${c.round} のコンタクトシート`}
                    className={styles.motionCandidateThumb}
                    onError={() => setBroken((prev) => new Set(prev).add(c.round))}
                  />
                ) : (
                  <span className={styles.motionCandidateNoImage}>画像なし</span>
                )}
              </button>
              <span className={styles.motionCandidateMeta}>
                版 {c.round}{scored ? `・${c.average.toFixed(1)} 点` : '・採点前'}{c.passed ? '・合格' : ''}
              </span>
              {canStop && (
                <button
                  type="button"
                  className={styles.motionCandidatePick}
                  onClick={() => setConfirm({ pick: c.round })}
                >
                  この版にする
                </button>
              )}
            </div>
          );
        })}
      </div>
      {canStop && !confirm && (
        <button type="button" className={styles.motionCandidateStopAll} onClick={() => setConfirm({})}>
          ここまでで決める
        </button>
      )}
      {canStop && confirm && (
        <div className={styles.jobQueueItemConfirm} role="group" aria-label="生成を止める確認" style={{ margin: '6px 0 0', paddingLeft: 10 }}>
          <span className={styles.jobQueueItemConfirmText}>
            {confirm.pick != null
              ? `版 ${confirm.pick} で決めて動画を描画します。これ以降の採点と修正は行いません（ポイントは変わりません）。`
              : 'ここまでの候補から最も良い版で決めて動画を描画します。これ以降の採点と修正は行いません（ポイントは変わりません）。'}
          </span>
          <span className={styles.jobQueueItemConfirmActions}>
            <button type="button" className={styles.jobQueueItemConfirmCancel} onClick={() => setConfirm(null)}>
              取消
            </button>
            <button
              type="button"
              className={styles.jobQueueItemConfirmOk}
              onClick={() => {
                const target = confirm;
                setConfirm(null);
                onStop(target.pick);
              }}
            >
              {confirm.pick != null ? 'この版にする' : 'ここで決める'}
            </button>
          </span>
        </div>
      )}
      {preview && createPortal(
        <div
          className={styles.motionCandidatePreviewOverlay}
          role="dialog"
          aria-label={`版 ${preview.round} のプレビュー`}
          onClick={() => setPreview(null)}
        >
          <div className={styles.motionCandidatePreviewCard} onClick={(e) => e.stopPropagation()}>
            {preview.contactSheetUrl && !broken.has(preview.round) ? (
              <img
                src={preview.contactSheetUrl}
                alt={`版 ${preview.round} のコンタクトシート`}
                className={styles.motionCandidatePreviewImg}
                onError={() => setBroken((prev) => new Set(prev).add(preview.round))}
              />
            ) : (
              <span className={styles.motionCandidateNoImage}>画像を読み込めませんでした</span>
            )}
            <div className={styles.motionCandidatePreviewInfo}>
              <strong>版 {preview.round}</strong>
              {isCandidateScored(preview) ? (
                <span>
                  平均 {preview.average.toFixed(1)} 点{preview.passed ? '・合格' : ''}
                  {' ('}
                  {Object.entries(preview.scores).map(([k, v]) => `${k} ${v}`).join(' / ')}
                  {')'}
                </span>
              ) : (
                <span>採点前</span>
              )}
              {preview.summary && <span>{preview.summary}</span>}
            </div>
            <button type="button" className={styles.jobQueueItemConfirmCancel} onClick={() => setPreview(null)} style={{ alignSelf: 'flex-end' }}>
              閉じる
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
